import React, { useEffect, useState } from "react";
import { ModalProps } from "../../../interfaces/modal";
import {
  addInvoiceTemplate,
  getInvoiceTemplate,
  updateInvoiceTemplate,
} from "../../../api/invoicesTemplates";
import { showToastError, showToastSuccess } from "../../../services/toast";
import { useSettingsStore } from "../../../stores/settings";
import { getFileNameFromResponse } from "../../../services/function";
import Dropzone from "react-dropzone";
import upload from "../../../assets/icons/darkIcons/download.svg";

const AddTemplate: React.FC<ModalProps> = ({ closeModal, id, update }) => {
  const { updateSettings, setUpdateSettings } = useSettingsStore();
  const [fileName, setFileName] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const modalTitle = update
    ? "Remplacement d'un template de facture"
    : "Ajout d'un template de facture";
  const actionLabel = update ? "Remplacer" : "Ajouter";

  useEffect(() => {
    if (update) {
      getInvoiceTemplate(id as number)
        .then((res) => {
          const fetchedFileName = getFileNameFromResponse(res);
          setFileName(fetchedFileName);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  }, [id, update]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleEsc);
    };
  }, [closeModal]);

  const submitTemplate = (file: File | null) => {
    if (!file) {
      showToastError("Veuillez sélectionner un fichier");
      return;
    }
    if (update && id) {
      updateInvoiceTemplate(id as number, file)
        .then(() => {
          showToastSuccess("Template remplacé avec succès");
          setUpdateSettings(!updateSettings);
          setSelectedFile(null);
          closeModal();
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    } else {
      addInvoiceTemplate(file)
        .then(() => {
          showToastSuccess("Template ajouté avec succès");
          setUpdateSettings(!updateSettings);
          setSelectedFile(null);
          closeModal();
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitTemplate(selectedFile);
  };

  const handleBackdropClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const sectionCardClass =
    "w-full border border-light-blue/50 rounded-2xl bg-white/80 shadow-lg shadow-light-blue/30 backdrop-blur-sm";

  return (
    <>
      <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="absolute inset-0 bg-grey/60 backdrop-blur-xs transition-opacity"
          aria-hidden="true"
          onClick={handleBackdropClick}
        />
        <form
          onSubmit={handleSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby="template-modal-title"
          className="relative z-10 w-full max-w-3xl rounded-3xl border border-light-blue/40 bg-white/95 px-6 py-6 text-grey shadow-2xl shadow-light-blue/50"
        >
          <header className="flex flex-col gap-3 border-b border-light-blue/30 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2
                  id="template-modal-title"
                  className="text-2xl font-semibold tracking-tight text-grey"
                >
                  {modalTitle}
                </h2>
                <span className="rounded-full bg-light-blue/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-light-blue">
                  {update ? "Edition" : "Création"}
                </span>
              </div>
              <p className="mt-1 text-sm text-grey/70">
                Glissez ou importez votre document Word pour mettre à jour vos
                templates.
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full border border-light-blue/40 text-grey transition hover:bg-light-blue/10"
            >
              <span className="sr-only">Fermer la fenêtre</span>
              <span aria-hidden="true" className="text-xl font-semibold">
                ×
              </span>
            </button>
          </header>

          <div className="custom-scroll mt-6 flex max-h-[50vh] flex-col gap-5 overflow-y-auto pr-1">
            <section className={`${sectionCardClass} p-5`}>
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-grey/70">
                Informations
              </h4>
              <hr className="mt-1 h-1 w-24 rounded bg-light-red border-light-red" />
              <div className="mt-4 space-y-3 text-sm text-grey/80">
                <p>
                  {update
                    ? `Merci de télécharger le nouveau template à la place de ${
                        fileName || "l'ancien fichier"
                      }.`
                    : "Merci de télécharger le template de facture que vous souhaitez ajouter."}
                </p>
                {update && (
                  <p className="text-xs italic text-grey/70">
                    ⚠️ Attention, les documents déjà générés conserveront
                    l'ancien modèle.
                  </p>
                )}
              </div>
            </section>

            <section className={`${sectionCardClass} p-5`}>
              <div className="flex flex-col gap-1">
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-grey/70">
                  Téléversement
                </h4>
                <hr className="h-1 w-24 rounded bg-light-red border-light-red" />
                <p className="text-xs text-grey/70 mt-2 italic">
                  Formats acceptés : .doc, .docx
                </p>
              </div>
              <div
                className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-light-blue/40 bg-light-blue/5 px-6 py-6 text-center text-grey transition hover:border-light-blue/70 hover:bg-light-blue/10"
              >
                <Dropzone
                  multiple={false}
                  accept={{
                    "application/msword": [".doc"],
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                      [".docx"],
                  }}
                  onDrop={(acceptedFiles) => {
                    const droppedFile = acceptedFiles[0] ?? null;
                    setSelectedFile(droppedFile);
                    submitTemplate(droppedFile);
                  }}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed w-full rounded-lg p-5 cursor-pointer transition-all duration-200 ${
                        isDragActive
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/50"
                      }`}
                    >
                      <input {...getInputProps()} />
                      <div className="flex flex-col items-center gap-2">
                        <div className={`rounded-full p-3 ${isDragActive ? "bg-indigo-100" : "bg-gray-200"}`}>
                          <img src={upload} alt="upload" className="w-6 h-6" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-gray-700 mb-0.5">
                            {isDragActive ? "Déposez le fichier ici" : "Glissez-déposez votre fichier"}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            ou cliquez pour sélectionner
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Dropzone>
                {selectedFile && (
                  <p className="mt-4 text-sm font-medium text-light-blue">
                    {selectedFile.name}
                  </p>
                )}
              </div>
            </section>
          </div>

          <footer className="mt-6 flex flex-col gap-3 border-t border-light-blue/30 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeModal}
              className="w-full rounded-xl border border-light-blue/40 px-4 py-2 text-sm font-semibold text-grey transition hover:bg-light-blue/10 sm:w-auto"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="w-full rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 sm:w-auto"
            >
              {actionLabel}
            </button>
          </footer>
        </form>
      </div>
    </>
  );
};

export default AddTemplate;
